<script setup lang="ts">
/**
 * The hub's reading options: the «Métricas globales» and the preferred method, both for the whole
 * platform.
 *
 * They used to sit in the hub's hero card. The card went at the product owner's request
 * (2026-10-14): the module nav carries the name, the connection and this button on the hub, and
 * folds back to the tabs everywhere else.
 *
 * ## Options instead of shortcuts
 *
 * The three shortcuts that sat in the hero (upload, create a group, new configuration) went at the
 * product owner's request (2026-09-27): each list screen keeps its own labelled button. A popover
 * and not a menu, as in the video review: a switch is tried while watching the figures change.
 */
import {
  ArButton,
  ArPopover,
  ArPopoverContent,
  ArPopoverTrigger,
  ArSwitch,
  ArToggleGroup,
  ArToggleGroupItem,
} from '@beonit/adaptive-raindrop';
import { SlidersHorizontal } from '@lucide/vue';
import { GLOBAL_METRICS } from '../../lib/metric-explanations.js';
import { METHOD_LABELS, METHOD_ORDER, type ScoreMethod } from '../../lib/metrics-series.js';
import { useGlobalMetrics } from '../use-global-metrics.js';
import { usePreferredMethod } from '../use-preferred-method.js';

/** The «Métricas globales»: every figure over the whole dataset. */
const globalMetrics = useGlobalMetrics();

/** The preferred method: the one every screen shows its figures under. */
const scoreMethod = usePreferredMethod();

/** A segmented control lets go of its value when the selected item is clicked again: keep it. */
function chooseMethod(value: unknown): void {
  if (typeof value === 'string' && METHOD_ORDER.includes(value as ScoreMethod)) {
    scoreMethod.value = value as ScoreMethod;
  }
}
</script>

<template>
  <ArPopover>
    <ArPopoverTrigger as-child>
      <ArButton variant="outline">
        <SlidersHorizontal />
        Opciones
      </ArButton>
    </ArPopoverTrigger>

    <ArPopoverContent class="flex w-80 flex-col gap-3" align="end">
      <p class="text-sm font-medium">Opciones de las métricas</p>

      <label class="flex items-start justify-between gap-3 text-sm">
        <span class="flex flex-col gap-1">
          {{ GLOBAL_METRICS }}
          <span class="text-xs text-muted-foreground">
            Calcula las cifras sobre todos los vídeos.
          </span>
        </span>
        <ArSwitch v-model="globalMetrics" :aria-label="GLOBAL_METRICS" />
      </label>

      <div class="flex flex-col gap-2 text-sm">
        <span class="flex flex-col gap-1">
          Método preferido
          <span class="text-xs text-muted-foreground">
            El método con el que se muestran las cifras.
          </span>
        </span>
        <ArToggleGroup
          type="single"
          variant="outline"
          size="sm"
          class="w-full"
          :model-value="scoreMethod"
          aria-label="Método preferido"
          @update:model-value="chooseMethod"
        >
          <ArToggleGroupItem
            v-for="methodKey of METHOD_ORDER"
            :key="methodKey"
            :value="methodKey"
            class="flex-1 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary data-[state=on]:hover:text-primary-foreground"
          >
            {{ METHOD_LABELS[methodKey] }}
          </ArToggleGroupItem>
        </ArToggleGroup>
      </div>
    </ArPopoverContent>
  </ArPopover>
</template>
